import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { PaperPlaneRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function MessagesDialog({ open, onClose, booking }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const load = async () => {
    if (!booking) return;
    try {
      const { data } = await api.get(`/bookings/${booking.id}/messages`);
      setMessages(data);
      setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 50);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, booking?.id]);

  const send = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      await api.post(`/bookings/${booking.id}/messages`, { body });
      setText("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const otherName = booking?.listing?.host?.id === user?.id
    ? booking?.guest?.name
    : booking?.listing?.host?.name;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0">
        <DialogHeader className="p-5 border-b border-neutral-200">
          <DialogTitle className="font-display font-medium text-xl">
            Message {otherName}
          </DialogTitle>
          <div className="text-xs text-neutral-500 mt-1">
            {booking?.listing?.title} · {booking?.date} · {booking?.start_hour?.toString().padStart(2,"0")}:00–{booking?.end_hour?.toString().padStart(2,"0")}:00
          </div>
        </DialogHeader>

        <div ref={listRef} data-testid="message-list" className="max-h-[60vh] min-h-[300px] overflow-y-auto p-5 space-y-3 bg-neutral-50">
          {messages.length === 0 ? (
            <div className="text-center text-sm text-neutral-500 py-16">
              No messages yet. Say hello — share directions, wifi, or anything they should know.
            </div>
          ) : messages.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-4 py-2 text-sm border ${mine ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200"}`}>
                  <div>{m.body}</div>
                  <div className={`text-[10px] mt-1 ${mine ? "text-neutral-400" : "text-neutral-500"}`}>
                    {m.sender?.name?.split(" ")[0]} · {formatTime(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={send} className="p-4 border-t border-neutral-200 flex gap-2">
          <Input
            data-testid="msg-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message"
            className="h-11 border-neutral-300 flex-1"
          />
          <Button data-testid="msg-send" type="submit" disabled={sending || !text.trim()} className="h-11 bg-neutral-900 hover:bg-neutral-800">
            <PaperPlaneRight size={16}/>
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatTime(iso) {
  try { return format(new Date(iso), "MMM d, HH:mm"); } catch { return ""; }
}
